<script setup lang="ts">
import type { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";

import { cn, walkTreeByLevel } from "@voltalis/frontend-utils";
import { IVirtualizedTreeNode, IVirtualizedTreeNodeModel } from "@voltalis/ui-kit/components";
import { PlusIcon } from "@voltalis/ui-kit/icons";
import { last, remove } from "es-toolkit";
import { computed, ref, watch } from "vue";

import {
  getTreeSectionAndDescendantSectionIds,
  getTreeBulkSectionSelectionState,
  toggleTreeBulkSectionSelection,
} from "@/app/tools/new-section-management/site-sections-tree/bulk-category-assignment/bulk-category-selection";
import { filterSectionForest } from "@/app/tools/new-section-management/site-sections-tree/filters/section-tree-filtering";
import { isPendingSiteSectionPreviewNode } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree-pending-preview";
import {
  TOpenedCreateSectionDrawerMethod,
  TSiteSectionsTreeData,
} from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import {
  type IPendingSiteSectionMovePreview,
  useSiteSectionsTreePendingPreview,
} from "@/app/tools/new-section-management/site-sections-tree/use-site-sections-tree-pending-preview";
import { useMoveSectionMutation } from "@/composables/kili/sections/mutations/use-move-section-mutation";
import { useUpdateSectionsCategoriesMutation } from "@/composables/kili/sections/mutations/use-update-sections-categories-mutation";
import { useManageSiteSectionCategories } from "@/composables/kili/sections/use-manage-site-section-categories";
import { useSiteSectionCategory } from "@/composables/kili/sections/use-site-section-category";
import { useGetSiteDetailsQuery } from "@/composables/kili/sites/queries/use-get-site-details-query";
import { useV4hSiteSectionsForestQuery } from "@/composables/kili/sites/queries/use-get-site-sections-forest-query";
import { useToaster } from "@/composables/layout/use-toaster";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { V4HSiteId, SiteSectionCategoryId, SiteSectionId } from "@/data/common/ids";
import { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import CreateSiteSectionDrawer from "@/app/tools/new-section-management/site-section-creation/create-site-section-drawer.vue";
import BulkCategoryAbandonDialog from "@/app/tools/new-section-management/site-sections-tree/bulk-category-assignment/bulk-category-abandon-dialog.vue";
import BulkCategoryToolbar from "@/app/tools/new-section-management/site-sections-tree/bulk-category-assignment/bulk-category-toolbar.vue";
import SiteSectionCategoryDrawer from "@/app/tools/new-section-management/site-sections-tree/categories/site-section-category-drawer.vue";
import SectionDetailsDrawer from "@/app/tools/new-section-management/site-sections-tree/section-details/section-details-drawer.vue";
import SiteSectionsTreeBulkDisplay from "@/app/tools/new-section-management/site-sections-tree/tree-display/site-sections-tree-bulk-display.vue";
import SiteSectionsTreeDefaultDisplay from "@/app/tools/new-section-management/site-sections-tree/tree-display/site-sections-tree-default-display.vue";
import BaseLayout from "@/components/layout/base-layout.vue";
import NewSiteDetailsCard from "@/components/tools/site-details/new-site-details-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const currentRouteName = computed(() => router.currentRoute.value.name);

const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");
const { t, k } = useTypedIntl();
const toast = useToaster();

const viewedSectionId = ref<SiteSectionId | undefined>();
const movedSectionId = ref<SiteSectionId | undefined>();
const isSectionDetailsDrawerVisible = ref<boolean>(false);

const { data: siteDetails, isLoading: isFetchingSiteDetails } = useGetSiteDetailsQuery(siteId);
const { data: siteSectionsForest, isLoading: isFetchingSiteSectionsForest } = useV4hSiteSectionsForestQuery(siteId);
const { mutateAsync: moveSection, isPending: isMovingSection } = useMoveSectionMutation(siteId, movedSectionId);
const { mutateAsync: bulkUpdateSectionsCategory, isPending: isBulkUpdatingSectionsCategories } =
  useUpdateSectionsCategoriesMutation(siteId);
const { assignSiteSectionCategory, isUpdatingSectionsCategories: isAssigningSectionCategory } =
  useSiteSectionCategory(siteId);

const isCreateSectionDrawerVisible = ref<boolean>(false);
const openedCreateSectionDrawerMethod = ref<TOpenedCreateSectionDrawerMethod | undefined>();
const isSectionCategoryDrawerVisible = ref<boolean>(false);
const sectionSearchQuery = ref<string>("");
const selectedApplianceCategoryFilters = ref<EProgramApplianceCategory[]>([]);
const selectedSectionCategories = ref<SiteSectionCategoryId[]>([]);
const maybePreSelectedParentForCreation = ref<SectionNode | null>(null);
const pendingMovePreview = ref<IPendingSiteSectionMovePreview | null>(null);
const {
  sectionCategories,
  sectionCategoryFilterOptions,
  isFetchingSectionCategories,
  isCreatingSectionCategory,
  isUpdatingSectionCategory,
  isDeletingSectionCategory,
  createSectionCategory,
  updateSectionCategory,
  deleteSectionCategory,
} = useManageSiteSectionCategories(siteId, selectedSectionCategories);

const isBulkCategoryEditionMode = ref<boolean>(false);
const isQuitBulkCategoryEditionConfirmationVisible = ref<boolean>(false);
const selectedBulkSectionIds = ref<Set<SiteSectionId>>(new Set());
const selectedBulkCategoryId = ref<SiteSectionCategoryId | undefined>();
const previewedBulkDescendantSectionIds = ref<Set<SiteSectionId>>(new Set());

const isLoading = computed<boolean>(() => isFetchingSiteDetails.value || isFetchingSiteSectionsForest.value);
const isTreeMutationPending = computed<boolean>(
  () => isMovingSection.value || isBulkUpdatingSectionsCategories.value || isAssigningSectionCategory.value,
);

const breadcrumbFormatter = computed(() => {
  return {
    [ERouteName.SITE_SECTIONS_TREE]: () => {
      return siteDetails.value?.name ?? t(k.data.consumptionMonitoring.siteMonitoring.title);
    },
  };
});

function mapSectionToVirtualizedTreeNode(sectionNode: SectionNode): IVirtualizedTreeNode<SectionNode> {
  return {
    key: sectionNode.id,
    name: sectionNode.name,
    state: {
      isExpanded: true,
    },
    children: sectionNode.children.map(mapSectionToVirtualizedTreeNode),
    data: sectionNode,
  };
}

const mappedSiteSectionTree = computed<IVirtualizedTreeNode<TSiteSectionsTreeData> | undefined>(() => {
  if (!siteSectionsForest.value || !siteDetails.value) return;

  return {
    name: siteDetails.value.name ?? "",
    state: {
      isExpanded: true,
    },
    children: siteSectionsForest.value.sections.map(mapSectionToVirtualizedTreeNode),
    data: null,
  };
});

const isFilteringByCategory = computed<boolean>(
  () => selectedApplianceCategoryFilters.value.length > 0 || selectedSectionCategories.value.length > 0,
);
const canDragAndDropSiteSections = computed<boolean>(() => !isFilteringByCategory.value);

const filteredMappedSiteSectionTree = computed<IVirtualizedTreeNode<TSiteSectionsTreeData> | undefined>(() => {
  if (!mappedSiteSectionTree.value) return;

  const filteredChildren = filterSectionForest(mappedSiteSectionTree.value.children, {
    searchQuery: sectionSearchQuery.value,
    selectedApplianceCategories: selectedApplianceCategoryFilters.value,
    selectedSectionCategories: selectedSectionCategories.value,
  });

  return {
    ...mappedSiteSectionTree.value,
    children: filteredChildren,
  };
});

const pendingCreationPreview = computed(() => {
  return {
    isVisible: isCreateSectionDrawerVisible.value,
    name: t(k.tools.siteManagement.siteTreeStructure.creation.pendingPreviewName),
    parent: maybePreSelectedParentForCreation.value,
  };
});

const { siteSectionsTreeWithPendingPreview } = useSiteSectionsTreePendingPreview({
  siteSectionsTree: mappedSiteSectionTree,
  filteredSiteSectionsTree: filteredMappedSiteSectionTree,
  creationPreview: pendingCreationPreview,
  movePreview: pendingMovePreview,
});

const visibleSectionsCount = computed<number>(() => {
  let count = 0;

  for (const section of walkTreeByLevel(siteSectionsTreeWithPendingPreview.value?.children)) {
    if (section.data && !isPendingSiteSectionPreviewNode(section)) count++;
  }

  return count;
});

const visibleSectionIds = computed<SiteSectionId[]>(() => {
  const result: SiteSectionId[] = [];

  for (const section of walkTreeByLevel(filteredMappedSiteSectionTree.value?.children)) {
    if (section.data) result.push(section.data.id);
  }

  return result;
});

const selectedBulkSectionsCount = computed<number>(() => selectedBulkSectionIds.value.size);

const hasSelectedBulkSections = computed<boolean>(() => selectedBulkSectionsCount.value > 0);

const areAllVisibleSectionsSelected = computed<boolean>(() => {
  return (
    visibleSectionIds.value.length > 0 &&
    visibleSectionIds.value.every((sectionId) => selectedBulkSectionIds.value.has(sectionId))
  );
});

const areSomeVisibleSectionsSelected = computed<boolean>(() => {
  return visibleSectionIds.value.some((sectionId) => selectedBulkSectionIds.value.has(sectionId));
});

const areVisibleSectionsPartiallySelected = computed<boolean>(() => {
  return areSomeVisibleSectionsSelected.value && !areAllVisibleSectionsSelected.value;
});

const selectedBulkCategoryIds = computed<SiteSectionCategoryId[]>({
  get: () => (selectedBulkCategoryId.value ? [selectedBulkCategoryId.value] : []),
  set: (categoryIds) => {
    selectedBulkCategoryId.value = last(categoryIds);
  },
});

const visibleSectionsCountLabel = computed<string>(() => {
  return t(k.tools.siteManagement.siteTreeStructure.visibleSectionsCount({ count: visibleSectionsCount.value }));
});

const hashmapFlattenedSections = computed<Map<SiteSectionId, SectionNode>>(() => {
  const result = new Map<SiteSectionId, SectionNode>();

  for (const section of walkTreeByLevel(siteSectionsForest.value?.sections)) {
    result.set(section.id, section);
  }

  return result;
});

const hasSelectedBulkSectionsWithCategory = computed<boolean>(() => {
  return [...selectedBulkSectionIds.value].some((sectionId) => hashmapFlattenedSections.value.get(sectionId)?.category);
});

// For now, we do not have a dedicated endpoint to fetch the details of a specific section
// so when the section gets edited, we have to fetch the site section forest then traverse it
// to find the updated section. The current solution here is not ideal since we're traversing
// the forest each time we retrieve the sections forest to build a hashmap
const viewedSection = computed<SectionNode | undefined>(() => {
  const sectionId = viewedSectionId.value;
  if (sectionId === undefined) return;

  return hashmapFlattenedSections.value.get(sectionId);
});

const maybeViewedSectionParentForEdition = computed<TSiteSectionsTreeData | undefined>(() => {
  if (!viewedSection.value) return;

  const maybeSectionParentId = viewedSection.value.parent?.id;
  if (!maybeSectionParentId) return null;

  return hashmapFlattenedSections.value.get(maybeSectionParentId);
});

function removeApplianceCategoryFromFilters(category: EProgramApplianceCategory) {
  remove(selectedApplianceCategoryFilters.value, (c) => c === category);

  MixpanelService.trackEvent(EMixpanelEvent.APPLIANCE_TYPE_FILTER_CLEARED, {
    page_context: currentRouteName.value?.toString(),
    method: "single",
  });
}

function removeSectionCategoryFromFilters(categoryId: SiteSectionCategoryId) {
  remove(selectedSectionCategories.value, (c) => c === categoryId);

  MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_CLEARED, {
    page_context: currentRouteName.value?.toString(),
    method: "single",
  });
}

async function handleCreateSectionCategory(name: string): Promise<boolean> {
  return createSectionCategory(name);
}

async function handleAssignSectionCategory(
  sectionId: SiteSectionId,
  sectionCategoryId: SiteSectionCategoryId | null,
): Promise<boolean> {
  if (isAssigningSectionCategory.value) return false;

  return assignSiteSectionCategory(sectionId, sectionCategoryId);
}

function handleAssignTreeNodeSectionCategory(
  section: TSiteSectionsTreeData,
  sectionCategoryId: SiteSectionCategoryId,
): Promise<boolean> {
  if (!section) return Promise.resolve(false);

  const nextSectionCategoryId = section.category?.id === sectionCategoryId ? null : sectionCategoryId;

  return handleAssignSectionCategory(section.id, nextSectionCategoryId);
}

function handleViewSection(section: SectionNode) {
  viewedSectionId.value = section.id;
  isSectionDetailsDrawerVisible.value = true;
}

function openCreateSectionDrawer(
  method: TOpenedCreateSectionDrawerMethod,
  section?: IVirtualizedTreeNode<TSiteSectionsTreeData>,
) {
  maybePreSelectedParentForCreation.value = section?.data ?? null;
  isCreateSectionDrawerVisible.value = true;
  openedCreateSectionDrawerMethod.value = method;
}

function enterBulkCategoryEdition(categoryId?: SiteSectionCategoryId) {
  resetBulkCategoryEdition();
  isCreateSectionDrawerVisible.value = false;
  isSectionCategoryDrawerVisible.value = false;
  selectedBulkCategoryId.value = categoryId;
  isBulkCategoryEditionMode.value = true;
}

function getBulkSectionSelectionState(section: IVirtualizedTreeNode<TSiteSectionsTreeData>) {
  return getTreeBulkSectionSelectionState(section, selectedBulkSectionIds.value);
}

function toggleBulkSectionSelection(section: IVirtualizedTreeNode<TSiteSectionsTreeData>, isSelected: boolean) {
  selectedBulkSectionIds.value = toggleTreeBulkSectionSelection(selectedBulkSectionIds.value, section, isSelected);
}

function toggleBulkSectionCardSelection(section: IVirtualizedTreeNode<TSiteSectionsTreeData>) {
  const { isSelected } = getBulkSectionSelectionState(section);

  toggleBulkSectionSelection(section, !isSelected);
}

function previewBulkSectionDescendantSelection(section: IVirtualizedTreeNode<TSiteSectionsTreeData>) {
  previewedBulkDescendantSectionIds.value = new Set(getTreeSectionAndDescendantSectionIds(section));
}

function resetBulkSectionDescendantSelectionPreview() {
  previewedBulkDescendantSectionIds.value = new Set();
}

function isBulkSectionDescendantSelectionPreviewed(sectionId: SiteSectionId) {
  return previewedBulkDescendantSectionIds.value.has(sectionId);
}

function toggleVisibleBulkSectionsSelection(isSelected: boolean) {
  const nextSelectedIds = new Set(selectedBulkSectionIds.value);

  visibleSectionIds.value.forEach((sectionId) => {
    if (isSelected) {
      nextSelectedIds.add(sectionId);
    } else {
      nextSelectedIds.delete(sectionId);
    }
  });

  selectedBulkSectionIds.value = nextSelectedIds;
}

function resetBulkCategoryEdition() {
  selectedBulkSectionIds.value = new Set();
  resetBulkSectionDescendantSelectionPreview();
  selectedBulkCategoryId.value = undefined;
  isQuitBulkCategoryEditionConfirmationVisible.value = false;
}

function exitBulkCategoryEdition() {
  resetBulkCategoryEdition();
  isBulkCategoryEditionMode.value = false;
}

function handleRequestExitBulkCategoryEdition() {
  if (!hasSelectedBulkSections.value) {
    exitBulkCategoryEdition();
    return;
  }

  isQuitBulkCategoryEditionConfirmationVisible.value = true;
}

async function applyBulkCategory(sectionCategoryId: SiteSectionCategoryId | null | undefined) {
  if (!hasSelectedBulkSections.value || isBulkUpdatingSectionsCategories.value) return;
  if (sectionCategoryId === undefined) return;

  const selectedSectionIdsArray = [...selectedBulkSectionIds.value];

  try {
    await bulkUpdateSectionsCategory({
      sectionsWithCategories: selectedSectionIdsArray.map((sectionId) => ({
        sectionId,
        sectionCategoryId,
      })),
    });

    toast.add({
      severity: "success",
      detail: t(k.tools.siteManagement.siteTreeStructure.bulkCategory.message.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_ASSIGNED_TO_SECTION, {
      action_type: "multiple",
      space_id: [...selectedSectionIdsArray],
      action: sectionCategoryId ? "assigned" : "removed",
      category_id: sectionCategoryId ?? undefined,
    });

    exitBulkCategoryEdition();
  } catch (error) {
    toastKiliError(error, "tools.siteManagement.siteTreeStructure.bulkCategory.message.error");
  }
}

async function handleMoveSection(
  draggedNode: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>,
  targetDropNode: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>,
) {
  if (isPendingSiteSectionPreviewNode(draggedNode) || isPendingSiteSectionPreviewNode(targetDropNode)) return;
  if (isMovingSection.value || !draggedNode.data) return;
  if (!canDragAndDropSiteSections.value) return;

  movedSectionId.value = draggedNode.data.id;

  try {
    await moveSection({ parentId: targetDropNode.data?.id ?? null });

    toast.add({
      severity: "success",
      detail: t(k.tools.siteManagement.moveSection.message.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_MOVED, {
      method: "drag_and_drop",
    });
  } catch {
    toast.add({
      severity: "error",
      detail: t(k.tools.siteManagement.moveSection.message.error),
    });
  }
}

function handleSectionDetailsDrawerHide() {
  viewedSectionId.value = undefined;
  pendingMovePreview.value = null;
}

watch(
  [
    sectionSearchQuery,
    selectedApplianceCategoryFilters,
    selectedSectionCategories,
    siteSectionsForest,
    sectionCategories,
  ],
  () => {
    if (!isBulkCategoryEditionMode.value) return;

    selectedBulkSectionIds.value = new Set();
    resetBulkSectionDescendantSelectionPreview();
  },
  { deep: true },
);

watch([visibleSectionIds, isBulkCategoryEditionMode], ([currentVisibleSectionIds, isEditionMode]) => {
  if (!isEditionMode) return;

  if (!currentVisibleSectionIds.length && selectedBulkSectionIds.value.size) {
    selectedBulkSectionIds.value = new Set();
  }
});
</script>

<template>
  <BaseLayout
    :breadcrumb-formatter="breadcrumbFormatter"
    :full-bleed="isBulkCategoryEditionMode"
    :hide-breadcrumb="isBulkCategoryEditionMode"
  >
    <BulkCategoryToolbar
      v-if="isBulkCategoryEditionMode"
      v-model:selected-category-ids="selectedBulkCategoryIds"
      :category-options="sectionCategoryFilterOptions"
      :create-category="createSectionCategory"
      :has-selected-sections="hasSelectedBulkSections"
      :has-selected-sections-with-category="hasSelectedBulkSectionsWithCategory"
      :is-creating-category="isCreatingSectionCategory"
      :is-updating-sections-categories="isBulkUpdatingSectionsCategories"
      :selected-sections-count="selectedBulkSectionsCount"
      @assign-category="applyBulkCategory"
      @remove-category="applyBulkCategory(null)"
      @request-exit="handleRequestExitBulkCategoryEdition"
    />

    <div :class="cn('flex flex-col', isBulkCategoryEditionMode ? 'min-h-0 flex-1 px-8 py-6' : 'h-full py-6 gap-8')">
      <div
        v-if="!isBulkCategoryEditionMode"
        class="flex justify-between items-center"
      >
        <router-link
          :to="{ name: ERouteName.SECTION_MANAGEMENT_NEW }"
          class="flex flex-row items-center gap-2"
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.backButton"
        >
          <i class="pi pi-arrow-left !text-xs" />
          <span class="font-semibold">{{ t(k.tools.siteManagement.siteTreeStructure.backToSitesList) }}</span>
        </router-link>

        <div class="flex items-center gap-3">
          <HospitalityButton
            :aria-label="t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.manage)"
            :data-test-id="DATA_TEST_IDS.views.siteSectionTree.manageCategoriesButton"
            severity="secondary"
            @click="isSectionCategoryDrawerVisible = true"
          >
            <i class="pi pi-tag pr-2" />
            {{ t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.manage) }}
          </HospitalityButton>

          <HospitalityButton
            :aria-label="t(k.tools.siteManagement.siteTreeStructure.addSection)"
            :data-test-id="DATA_TEST_IDS.views.siteSectionTree.addSectionButton"
            :icon="PlusIcon"
            :label="t(k.tools.siteManagement.siteTreeStructure.addSection)"
            @click="openCreateSectionDrawer('root_button')"
          />
        </div>
      </div>

      <NewSiteDetailsCard
        v-if="!isBulkCategoryEditionMode"
        :site-id="siteId"
        :site-details="siteDetails"
      />

      <div class="flex flex-row h-full w-full">
        <div class="site-sections-tree relative h-full flex flex-1">
          <SiteSectionsTreeDefaultDisplay
            v-if="!isBulkCategoryEditionMode"
            v-model:search-query="sectionSearchQuery"
            v-model:selected-appliance-categories="selectedApplianceCategoryFilters"
            v-model:selected-section-categories="selectedSectionCategories"
            :appliance-filter-site-sections-tree="mappedSiteSectionTree"
            :assign-section-category="handleAssignTreeNodeSectionCategory"
            :can-drag-and-drop-site-sections="canDragAndDropSiteSections"
            :category-options="sectionCategoryFilterOptions"
            :create-category="handleCreateSectionCategory"
            :hide-root-node="isFilteringByCategory"
            :is-creating-category="isCreatingSectionCategory"
            :is-loading="isLoading"
            :is-tree-mutation-pending="isTreeMutationPending"
            :site-id="siteId"
            :site-sections-tree="siteSectionsTreeWithPendingPreview"
            :visible-sections-count-label="visibleSectionsCountLabel"
            @dropped="handleMoveSection"
            @enter-bulk-category-edition="enterBulkCategoryEdition"
            @open-create-section-drawer="openCreateSectionDrawer('tree_view', $event)"
            @remove-appliance-category="removeApplianceCategoryFromFilters"
            @remove-section-category="removeSectionCategoryFromFilters"
            @view-section="handleViewSection"
          />

          <SiteSectionsTreeBulkDisplay
            v-else
            v-model:search-query="sectionSearchQuery"
            v-model:selected-appliance-categories="selectedApplianceCategoryFilters"
            v-model:selected-section-categories="selectedSectionCategories"
            :appliance-filter-site-sections-tree="mappedSiteSectionTree"
            :are-all-visible-sections-selected="areAllVisibleSectionsSelected"
            :are-visible-sections-partially-selected="areVisibleSectionsPartiallySelected"
            :category-options="sectionCategoryFilterOptions"
            :create-category="createSectionCategory"
            :get-section-selection-state="getBulkSectionSelectionState"
            :hide-root-node="isFilteringByCategory"
            :is-creating-category="isCreatingSectionCategory"
            :is-loading="isLoading"
            :is-section-previewed="isBulkSectionDescendantSelectionPreviewed"
            :site-id="siteId"
            :site-sections-tree="filteredMappedSiteSectionTree"
            :visible-sections-count-label="visibleSectionsCountLabel"
            @preview-section-selection="previewBulkSectionDescendantSelection"
            @remove-appliance-category="removeApplianceCategoryFromFilters"
            @remove-section-category="removeSectionCategoryFromFilters"
            @reset-section-selection-preview="resetBulkSectionDescendantSelectionPreview"
            @toggle-section-card-selection="toggleBulkSectionCardSelection"
            @toggle-visible-sections-selection="toggleVisibleBulkSectionsSelection"
            @update-section-selection="toggleBulkSectionSelection"
          />

          <div
            v-if="isTreeMutationPending"
            class="absolute inset-0 z-10 bg-white/50"
          />
        </div>

        <CreateSiteSectionDrawer
          v-model:visible="isCreateSectionDrawerVisible"
          :siteDetails="siteDetails"
          :isLoading="isFetchingSiteDetails"
          :opened-create-section-drawer-method="openedCreateSectionDrawerMethod"
          :maybePreSelectedParent="maybePreSelectedParentForCreation"
          :section-category-options="sectionCategoryFilterOptions"
          :siteSectionsTree="mappedSiteSectionTree"
          @select-parent="maybePreSelectedParentForCreation = $event"
        />
      </div>
    </div>
  </BaseLayout>

  <SiteSectionCategoryDrawer
    v-model:visible="isSectionCategoryDrawerVisible"
    :categories="sectionCategories ?? []"
    :is-loading="isFetchingSectionCategories"
    :is-creating-category="isCreatingSectionCategory"
    :is-updating-category="isUpdatingSectionCategory"
    :is-deleting-category="isDeletingSectionCategory"
    :create-category="createSectionCategory"
    :update-category="updateSectionCategory"
    :delete-category="deleteSectionCategory"
    @assign-category="enterBulkCategoryEdition"
  />

  <SectionDetailsDrawer
    v-model="isSectionDetailsDrawerVisible"
    :is-loading="isMovingSection"
    :section="viewedSection"
    :section-parent="maybeViewedSectionParentForEdition"
    :section-category-options="sectionCategoryFilterOptions"
    :site-details="siteDetails"
    :site-id="siteId"
    :siteSectionsTree="mappedSiteSectionTree"
    @hide="handleSectionDetailsDrawerHide"
    @pending-move-preview-change="pendingMovePreview = $event"
  />

  <BulkCategoryAbandonDialog
    v-model:visible="isQuitBulkCategoryEditionConfirmationVisible"
    @abandon="exitBulkCategoryEdition"
  />
</template>

<style lang="scss">
.site-sections-tree {
  .site-tree-structure {
    @apply absolute inset-0;
  }

  .pending-site-section-preview {
    @apply pointer-events-none;
  }

  .site-section-item:has(.pending-site-section-preview) {
    @apply border border-dashed border-brand bg-white;

    > :first-child:not(:has(.pending-site-section-preview)) {
      @apply hidden;
    }
  }
}
</style>
